import {
  canonical,
  createRecoveryFile,
  createRecoveryKeys,
  newDataKey,
  openRecoveryFile,
  type RecoveryPayload,
  signChallenge,
} from "./cryptoV2";

// Temporary setup state. A verified kit is disposed immediately after the
// authorized operation; no recovery private key is saved in the device vault.
export class RecoveryDraft {
  private verified = false;
  private payload?: RecoveryPayload;
  readonly publicKey: JsonWebKey;
  readonly file: string;
  private constructor(payload: RecoveryPayload, publicKey: JsonWebKey, file: string) {
    this.payload = payload;
    this.publicKey = publicKey;
    this.file = file;
  }
  static async prepare(
    scope: Pick<RecoveryPayload, "datasetId" | "generation" | "keyVersion">,
    password: string,
    material = newDataKey(),
  ) {
    const keys = await createRecoveryKeys();
    const payload = {
      datasetId: scope.datasetId,
      generation: scope.generation,
      keyVersion: scope.keyVersion,
      dataKey: material,
      recoverySecret: newDataKey(),
      recoveryPrivateKey: await crypto.subtle.exportKey("jwk", keys.privateKey),
    };
    return new RecoveryDraft(
      payload,
      await crypto.subtle.exportKey("jwk", keys.publicKey),
      await createRecoveryFile(payload, password),
    );
  }
  async verify(file: string, password: string) {
    this.verified = false;
    if (!this.payload) throw new Error("sync-recovery-draft-closed");
    const read = await openRecoveryFile(file, password, this.payload.datasetId);
    if (canonical(read) !== canonical(this.payload)) throw new Error("sync-recovery-file-mismatch");
    this.verified = true;
  }
  verifiedMaterial() {
    if (!this.verified || !this.payload) throw new Error("sync-recovery-file-verify-required");
    return {
      datasetId: this.payload.datasetId,
      generation: this.payload.generation,
      keyVersion: this.payload.keyVersion,
      dataKey: this.payload.dataKey,
    };
  }
  async sign(value: unknown) {
    this.verifiedMaterial();
    const key = await crypto.subtle.importKey(
      "jwk",
      this.payload!.recoveryPrivateKey,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign"],
    );
    return signChallenge(key, value);
  }
  dispose() {
    this.verified = false;
    this.payload = undefined;
  }
}
